import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, ArrowRight, BarChart3, Users, FileText, TrendingUp } from 'lucide-react';

interface ReviewDashboardStepProps {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  organizationId?: string;
}

export function ReviewDashboardStep({ onNext, organizationId }: ReviewDashboardStepProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleComplete = async () => {
    setIsLoading(true);
    
    try {
      // Track completion
      await supabase
        .from('onboarding_analytics')
        .insert({
          user_id: user!.id,
          event_name: 'onboarding_completed',
          event_data: { organization_id: organizationId }
        });

      onNext();
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: BarChart3,
      title: 'Cap Table Management',
      description: 'View and manage your equity distribution in real-time',
      link: '/dashboard'
    },
    {
      icon: Users,
      title: 'Team Equity',
      description: 'Grant options, track vesting, and manage employee equity',
      link: '/dashboard/equity'
    },
    {
      icon: FileText,
      title: 'Document Generation',
      description: 'Create and sign equity agreements with built-in e-signatures',
      link: '/dashboard/documents'
    },
    {
      icon: TrendingUp,
      title: 'Simulations & Modeling',
      description: 'Run what-if scenarios for funding rounds and exits',
      link: '/dashboard/simulations'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="p-4 bg-green-100 dark:bg-green-900/20 rounded-full">
            <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
          </div>
        </div>
        
        <div>
          <h2 className="text-2xl font-bold mb-2">You're all set!</h2>
          <p className="text-muted-foreground">
            Your organization is ready. Here's what you can do next:
          </p>
        </div>
      </div>

      <div className="grid gap-4">
        {features.map((feature, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-base">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <feature.icon className="h-5 w-5 text-primary" />
                </div>
                {feature.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>{feature.description}</CardDescription>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-primary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="text-base">Getting Started Checklist</CardTitle>
          <CardDescription>
            Track your progress as you explore Share Lattice
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            You'll find a personalized checklist on your dashboard to help guide you through 
            key features and ensure you're getting the most out of Share Lattice.
          </p>
        </CardContent>
      </Card>

      <div className="pt-4">
        <Button 
          onClick={handleComplete} 
          className="w-full" 
          size="lg"
          disabled={isLoading}
        >
          {isLoading ? 'Completing Setup...' : 'Go to Dashboard'}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}